import { DownloadIcon } from '@radix-ui/react-icons';
import { Badge, Flex } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { DialogButton } from 'components/common/dialogs/button';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { FlexTableWrapper } from 'components/common/layout/flex-table-wrapper';
import { CommonTableHoC } from 'components/common/table';
import { MachineSettingsDateRangeInput } from 'components/sections/admin-portal/machines/dialogs/settings/common/machine-settings-date-range-input';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { endOfDay, startOfDay } from 'date-fns';
import { ACTIONS_KEY } from 'enums/tables';
import usePrevious from 'hooks/usePrevious';
import { t } from 'i18next';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITableInfiniteLoader } from 'interfaces/tables/infinite-loader';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { AwsRegion } from 'lib_ts/enums/s3.enum';
import { IDateRangeValue } from 'lib_ts/interfaces/forms';
import { LOGGER_NAMES, LoggerName } from 'lib_ts/interfaces/i-machine-daemons';
import { IMachineLogFile } from 'lib_ts/interfaces/i-machine-log-file';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AdminMachinesService } from 'services/admin/machines.service';
import { MainService } from 'services/main.service';

const NUM_MACHINE_PROCESSES = LOGGER_NAMES.length;
const RECORDS_PER_REQUEST = NUM_MACHINE_PROCESSES * 2;

interface IFilters {
  dateRange?: {
    start: Date;
    end: Date;
  };
  process: LoggerName[];
}

export const ArchivedLogsTab = () => {
  const machine = useMachinesPortalStore((s) => s.machine);

  const machineID = machine?.machineID;

  const [loading, setLoading] = useState(false);

  const [data, setData] = useState<IMachineLogFile[]>([]);
  const [total, setTotal] = useState(0); // total number of logs matching the filters, as provided by the server

  const [filters, setFilters] = useState<IFilters>({
    dateRange: undefined,
    process: [],
  });

  const initializedRef = useRef(false);
  const prevFilters = usePrevious<IFilters>(filters);

  const downloadCallback = useCallback(async (path: string) => {
    try {
      const result = await MainService.getInstance().signUrls([
        {
          region: AwsRegion.CA,
          path: path,
        },
      ]);

      const entry = result?.[path];

      if (!entry) {
        NotifyHelper.error({ message_md: t('common.request-failed-msg') });
        return;
      }

      window.open(entry.url);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const columns = useMemo<ITypedTableColumn<IMachineLogFile>[]>(
    () => [
      {
        label: 'Date',
        key: 'date',
      },
      {
        label: 'Process',
        key: 'process',
        formatFn: (m) => <Badge color={RADIX.COLOR.WARNING}>{m.process}</Badge>,
      },
      {
        label: 'common.actions',
        key: ACTIONS_KEY,
        formatFn: (m) => (
          <DialogButton
            icon={<DownloadIcon />}
            label="common.download"
            color={RADIX.COLOR.INFO}
            onClick={() => downloadCallback(m.path)}
          />
        ),
      },
    ],
    [downloadCallback]
  );

  const loadData = useCallback(
    async (skip: number, silently?: boolean) => {
      if (!machineID) {
        return;
      }

      if (!silently) {
        setLoading(true);
      }

      const coercedDateRange = (): IDateRangeValue | undefined => {
        if (!filters.dateRange) {
          return undefined;
        }

        return {
          start: filters.dateRange.start.toISOString(),
          end: filters.dateRange.end.toISOString(),
        };
      };

      const coercedProcesses = (): LoggerName[] | undefined => {
        if (!filters.process.length) {
          return undefined;
        }

        return filters.process;
      };

      try {
        const { total: dbTotal, data: results } =
          await AdminMachinesService.getInstance().getLogs({
            machineID,
            skip,
            limit: RECORDS_PER_REQUEST,
            filters: {
              dateRange: coercedDateRange(),
              processes: coercedProcesses(),
            },
          });

        setTotal(dbTotal);

        if (skip > 0) {
          setData((prev) => [...prev, ...results]);
          return;
        }

        setData(results);
      } catch (error) {
        console.error(error);
      } finally {
        if (!silently) {
          setLoading(false);
        }
      }
    },
    [machineID, filters.dateRange, filters.process]
  );

  useEffect(() => {
    if (initializedRef.current) {
      return;
    }

    loadData(0);
    initializedRef.current = true;
  }, [loadData]);

  useEffect(() => {
    if (!initializedRef.current) {
      return;
    }

    // queries can be expensive, so we'll do a manual equality check here just in case
    // todo: is this actually necessary?
    if (!prevFilters || isEqual(prevFilters, filters)) {
      return;
    }

    loadData(0);
  }, [filters, loadData]);

  const onFilter = (v: Partial<IFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...v,
    }));
  };

  const tableInfiniteScrollable: ITableInfiniteLoader = {
    onLoadMore: () => loadData(data.length, true),
    hasMore: data.length < total,
    rootMargin: '128px',
  };

  return (
    <ErrorBoundary identifier="MachineArchivedLogsTab">
      <Flex
        direction="column"
        gap={RADIX.FLEX.GAP.MD}
        className={RADIX.VFLEX.GROW}
      >
        <FlexTableWrapper
          gap={RADIX.FLEX.GAP.SECTION}
          header={
            <CommonFormGrid columns={2}>
              <MachineSettingsDateRangeInput
                dateRange={filters.dateRange}
                onChange={(dateRange) => {
                  if (!dateRange) {
                    onFilter({ dateRange: undefined });
                    return;
                  }

                  onFilter({
                    dateRange: {
                      start: startOfDay(dateRange.start),
                      end: endOfDay(dateRange.end),
                    },
                  });
                }}
              />
              <CommonSearchInput
                id="archived-logs-processes"
                placeholder="Processes"
                options={LOGGER_NAMES.map((o) => ({
                  label: o,
                  value: o,
                }))}
                values={filters.process}
                onChange={(v) => onFilter({ process: v as LoggerName[] })}
                multiple
              />
            </CommonFormGrid>
          }
          table={
            <CommonTableHoC
              dataTestID="MachineArchivedLogs"
              identifier="MachineArchivedLogsTable"
              displayColumns={columns}
              displayData={data}
              rowClassNameFn={(m, i) => {
                if (!i) {
                  return '';
                }

                // add a small divider between each batch of logs for readability
                const prevRowDate = data[i - 1]?.date;
                if (!prevRowDate) {
                  return '';
                }

                const currRowDate = m.date;

                if (prevRowDate !== currRowDate) {
                  return 'divider-row';
                }

                return '';
              }}
              loading={loading}
              {...tableInfiniteScrollable}
              vFlex
            />
          }
        />
      </Flex>
    </ErrorBoundary>
  );
};
